import { Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, ListOrdered, UtensilsCrossed, Boxes, BadgePercent, Receipt, BarChart3, Clock, Printer, ListTree, CalendarClock, Store, Upload, ScrollText, Ticket, FileText, PackageSearch } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import NotificationGate from "../../components/NotificationGate";
import OrderAlertFeed from "../../components/OrderAlertFeed";
import PersistentAlertSetting from "../../components/PersistentAlertSetting";
import { hasCompletedPermissions } from "../PermissionsPage";
import { useTenant } from "../../contexts/TenantContext";
import { lseOn } from "../../lib/lse";

export default function MerchantLayout() {
    const { user, loading, logout } = useAuth();
    const nav = useNavigate();
    const { pathname, search } = useLocation();
    const { tenant } = useTenant();
    const isAuthorized = !!user && ["RESTAURANT_OWNER", "TENANT_ADMIN", "SUPER_ADMIN", "ZONE_ADMIN"].includes(user.role);

    useEffect(() => {
        if (!loading && !isAuthorized) nav("/login", { state: { role: "RESTAURANT_OWNER" } });
    }, [loading, isAuthorized, nav]);

    // iter181 — a branch (zone) admin's access to this console is NONE / VIEW / EDIT, decided by the business admin
    // and enforced by the API. The layout only says so: without it a view-only admin saw every edit control and an
    // error on each save, and "no access" rendered an empty console full of failed requests.
    const [branchAccess, setBranchAccess] = useState(null);
    const isBranchAdmin = user?.role === "ZONE_ADMIN";
    useEffect(() => {
        if (!isBranchAdmin) { setBranchAccess(null); return undefined; }
        let off = false;
        api.get("/zone-admin/me").then(({ data }) => { if (!off) setBranchAccess((data?.shop_access || "VIEW").toUpperCase()); })
            .catch(() => { if (!off) setBranchAccess("UNKNOWN"); /* the API still enforces it */ });
        return () => { off = true; };
    }, [isBranchAdmin]);

    // First-launch permissions onboarding for merchant app (camera +
    // notifications). Gated after auth so it doesn't interrupt the
    // login redirect flow.
    if (isAuthorized && !hasCompletedPermissions()) {
        const nextUrl = pathname + (search || "");
        return <Navigate to={`/onboarding/permissions?app=merchant&next=${encodeURIComponent(nextUrl)}`} replace />;
    }

    const isZoneAdmin = user?.role === "ZONE_ADMIN";
    const items = [
        { to: "/merchant", icon: LayoutDashboard, label: "Dashboard", end: true },
        ...(isZoneAdmin ? [] : [{ to: "/merchant/shops", icon: Store, label: "Shops" }]),
        { to: "/merchant/orders", icon: ListOrdered, label: "Orders" },
        // Location Service Engine picking (only for tenants with it on; shop owners see their own shops' orders).
        ...(!isZoneAdmin && lseOn(tenant, "warehouse_picking") ? [{ to: "/merchant/picking", icon: PackageSearch, label: "Picking" }] : []),
        ...(isZoneAdmin ? [] : [{ to: "/merchant/bookings", icon: CalendarClock, label: "Bookings" }]),
        { to: "/merchant/categories", icon: ListTree, label: "Categories" },
        { to: "/merchant/menu", icon: UtensilsCrossed, label: "Menu" },
        { to: "/merchant/inventory", icon: Boxes, label: "Inventory" },
        { to: "/merchant/offers", icon: BadgePercent, label: "Offers" },
        ...(isZoneAdmin ? [] : [{ to: "/merchant/coupons", icon: Ticket, label: "Shop Coupons" }]),
        ...(isZoneAdmin ? [] : [
            { to: "/merchant/settlements", icon: Receipt, label: "Settlements" },
            { to: "/merchant/commission-invoices", icon: FileText, label: "Commission Invoices" },
        ]),
        { to: "/merchant/analytics", icon: BarChart3, label: "Analytics" },
        ...(isZoneAdmin ? [
            { to: "/zone-admin", icon: ScrollText, label: "My Earnings" },
        ] : [
            { to: "/merchant/hours", icon: Clock, label: "Hours & Setup" },
            { to: "/merchant/printer", icon: Printer, label: "Print Queue" },
            { to: "/merchant/imports", icon: Upload, label: "Bulk Import" },
            { to: "/merchant/audit-log", icon: ScrollText, label: "Audit Log" },
        ]),
    ];

    if (!isAuthorized) return null;
    return (
        <AdminShell
            items={items}
            user={user}
            title="Merchant"
            testidPrefix="merchant-nav"
            sidebarExtra={<PersistentAlertSetting />}
            onLogout={async () => { try { await logout(); } finally { nav("/login"); } }}
        >
            <OrderAlertFeed />
            <NotificationGate />
            {isBranchAdmin && branchAccess === "VIEW" && (
                <p data-testid="branch-view-only" className="mb-3 text-xs rounded-xl bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900 px-3 py-2">
                    View only — you can see the shops of your branch, but changes are switched off for your account. Ask the business admin for edit access.
                </p>
            )}
            {isBranchAdmin && branchAccess === "NONE" ? (
                <div data-testid="branch-no-access" className="bg-card border border-dashed border-border rounded-2xl p-8 text-center text-sm text-muted-foreground">
                    <p className="font-semibold text-foreground">Shop access is switched off for your account.</p>
                    <p className="mt-1">Your earnings and drivers are on the branch page.</p>
                    <button type="button" onClick={() => nav("/zone-admin")} data-testid="branch-go-home"
                            className="mt-4 bg-primary text-primary-foreground rounded-xl px-5 min-h-[40px] text-sm font-semibold">Open branch page</button>
                </div>
            ) : (isBranchAdmin && branchAccess === null)
                // Not known yet: the pages wait. Rendering them first let a "no access" admin's pages fire a burst of
                // refused requests (and unhandled errors) before the notice replaced them.
                ? <p data-testid="branch-access-loading" className="py-12 text-center text-sm text-muted-foreground">Loading…</p>
                : <Outlet />}
        </AdminShell>
    );
}
